<template>
    <div 
        class="expire-action-spec-single"
        :class="{ 'editing': localEditMode, 'shadow-sm': localEditMode, 'locked': localEditMode, 'pending-delete': showPendingDelete, 'init-edit': initEdit }"
    >
        <transition name="fade-fast" mode="out-in">
            <div v-if="initEdit" :key="`${eas.localId}_init`" class="d-flex align-items-center justify-content-center min-height-150">
                <b-spinner></b-spinner>
            </div>
            <div v-else-if="showConfigSelectArea" :key="`${eas.localId}_config_select`">
                <div v-if="!initialConfigsLoaded" key="loading-configs" class="d-flex align-items-center justify-content-center min-height-150">
                    <b-spinner></b-spinner>
                </div>
                <div v-else-if="loadedNoConfigs" key="no-configs" class="min-height-150 d-flex flex-column align-items-center justify-content-center">
                    <div class="text-muted text-center font-weight-bold">No actions are currently available for this {{ isCompItem ? 'Document Type' : 'Document' }}.</div>
                    <div class="text-muted text-center font-size-80 mt-2">This may be because expiration tracking is not enabled, or because of your role.</div>
                    <div class="d-flex align-items-center justify-content-center mt-3">
                        <b-button
                        variant="minimal-primary"
                        :disabled="anyLoading"
                        @click="cancelEasEdit"
                        >OK, Cancel</b-button>
                    </div>
                </div>
                <div v-else key="select-config" class="w-100">
                    <div class="w-100 d-flex align-items-center gap-3">
                        <hr class="flex-fill">
                        <h6 class="mb-0 special-text text-muted">Select Action To Add</h6>
                        <hr class="flex-fill">
                    </div>
                    <div class="d-flex flex-wrap gap-3">
                        <b-button 
                            v-for="config in configs" 
                            :key="config.type_key" 
                            variant="light-primary" 
                            size="lg"
                            :disabled="config.disabled || anyLoading"
                            class="flex-fill min-width-100 min-height-100 border d-flex align-items-center justify-content-center"
                            @click="handleConfigSelect(config)"
                        >
                            <div class="max-width-200 d-flex align-items-center justify-content-center flex-column gap-2">
                                {{ config.type_label }}
                                <div class="text-muted font-size-70">{{ config.type_description }}</div>
                            </div>
                        </b-button>
                    </div>
                    <div class="d-flex align-items-center justify-content-center mt-2">
                        <b-button
                        variant="minimal-primary"
                        :disabled="anyLoading"
                        @click="cancelEasEdit"
                        >Cancel</b-button>
                    </div>
                </div>
            </div>
            <div v-else-if="localEditMode" :key="`${eas.localId}_edit`" class="">
                <b-overlay
                    :show="anyLoading"
                    :variant="deleting ? 'danger' : 'light'"
                    :opacity="0.85"
                    rounded="md"
                >
                    <div class="d-flex flex-column align-items-center justify-content-between gap-3">
                        <div class="w-100 px-3">
                            <h5 class="text-dark mt-1 mb-2">{{ isPersisted ? 'Edit' : 'Add' }} {{ selectedConfig.type_label }}</h5>
                            <p class="text-muted">{{ selectedConfig.type_description }}</p>
                        </div>
                        <b-alert :show="eas.has_validation_error" variant="danger" class="mb-2">{{ eas.last_validation_error }}</b-alert>
                        <div class="w-100 flex-fill px-3">
                            <!-- <div class="mr-1">Assign reminder task to me</div> -->  <!-- TODO replace this with action_type -->
                            <!-- TODO - make presets for things based on what's available, with big boxes to pick from instead of all the selects -->
                            <!-- TODO - add content/title fields for writing notes for emails and reminder tasks -->
                            <div class="">
                                <b-form-group 
                                    label="When:"
                                    label-class="eas-form-label group-label" 
                                    :label-cols="narrow ? 12 : 2"
                                    class="w-100"
                                    :class="narrow ? 'mb-0' : ''"
                                >
                                    <div class="d-flex align-items-center gap-2">
                                        <b-form-group 
                                            label="Timeframe" 
                                            label-class="eas-form-label"
                                            class="w-30 flex-shrink-1"
                                        >
                                            <b-input-group>
                                                <b-form-input
                                                    v-model="eas.duration_num_periods"
                                                    type="number"
                                                    class=""
                                                    min="0"
                                                    step="1"
                                                ></b-form-input>
                                                <b-form-select
                                                    v-model="eas.duration_period"
                                                    :options="duration_period_options"
                                                    text-field="label"
                                                    class=""
                                                ></b-form-select>
                                            </b-input-group>
                                        </b-form-group>
                                        <!-- TODO - group timeframe stuff together - before expired, after expire (once), on expire - that's pretty much all you have right now  -->
                                        <b-form-group
                                            label="Before/After"
                                            label-class="eas-form-label"
                                            class="w-40"
                                        >
                                            <b-form-select
                                                v-model="eas.schedule_type_value"
                                                :options="schedule_type_options"
                                                text-field="label"
                                                class="flex-shrink-1"
                                            ></b-form-select>
                                        </b-form-group>
                                        <b-form-group
                                            label="Relative to"
                                            label-class="eas-form-label"
                                            class="w-30"
                                        >
                                            <b-form-select
                                                v-model="eas.event_type_value"
                                                :options="event_type_options"
                                                text-field="label"
                                                class="flex-shrink-1"
                                            ></b-form-select>
                                        </b-form-group>
                                    </div>
                                </b-form-group>
                                <!-- <div class="ml-1">before expiration</div> -->
                            </div>
                            <div class="">
                                <b-form-group 
                                    label="Recipients / Assignees:"
                                    label-class="eas-form-label group-label" 
                                    :label-cols="narrow ? 12 : 2"
                                    class="w-100"
                                    :class="narrow ? 'mb-0' : ''"
                                >
                                    <div class="d-flex align-items-center gap-2">
                                        <!-- TODO - group all recipient things together into a few presets: Current TU, Admin Role, Other Roles (including supplier if available)  -->
                                        <b-form-group
                                            label="Recipient Type"
                                            label-class="eas-form-label"
                                            description="The type of recipient for this action"
                                            class="w-40"
                                        >
                                            <b-form-select
                                                v-model="eas.recipient_type_value"
                                                :options="recipient_type_options"
                                                text-field="label"
                                                class="flex-shrink-1"
                                            ></b-form-select>
                                        </b-form-group>
                                        <b-form-group
                                            v-if="useCompItemRole"
                                            label="Recipient Group / Role"
                                            label-class="eas-form-label"
                                            description="Select the group/role (from this Document Type) to assign to"
                                            class="w-60"
                                        >
                                            <b-form-select
                                                v-model="eas.recipient_object_role_id"
                                                :options="recipient_object_role_options"
                                                text-field="label"
                                                class="flex-shrink-1"
                                            ></b-form-select>
                                        </b-form-group>
                                        <b-form-group
                                            v-if="useSpecificTu"
                                            label="Recipient"
                                            label-class="eas-form-label"
                                            description="Select the specific user to assign to"
                                            class="w-60"
                                        >
                                            <b-form-select
                                                v-model="eas.recipient_tu_id"
                                                :options="recipientTuOptions"
                                                text-field="label"
                                                class="flex-shrink-1"
                                            ></b-form-select>
                                        </b-form-group>
                                    </div>
                                </b-form-group>
                            </div>
                            <div v-if="showRecipientEmailInput" class="">
                                <b-form-group 
                                    label="Email Recipients:"
                                    label-sr-only
                                    label-class="eas-form-label group-label" 
                                    description="Optional email addresses (only for people who are not Supplios users)"
                                    :label-cols="narrow ? 12 : 2"
                                    class="w-100"
                                >
                                    <EmailAddressListInput
                                        v-model="eas.recipient_emails"
                                        :label="isCompItem ? 'Additional Email Recipients (NO SUPPLIER EMAILS)' : 'Additional Email Recipients'"
                                        label-class="eas-form-label"
                                        :placeholder="'Enter email address and press Enter...'"
                                        class="mb-1"
                                    ></EmailAddressListInput>
                                    <b-form-text v-if="isCompItem">NOTE: Do NOT add supplier emails here, since this will apply to all Documents of this Document Type.</b-form-text>
                                </b-form-group>
                            </div>
                            <div v-if="showTitleInput || showContentInput" class="">
                                <b-form-group 
                                    label="With these details:"
                                    label-class="eas-form-label group-label" 
                                    :label-cols="narrow ? 12 : 2"
                                    class="w-100"
                                >
                                    <b-form-group 
                                        v-if="showTitleInput"
                                        :label="selectedConfig.title_label || 'Title'"
                                        label-class="eas-form-label" 
                                        :description="selectedConfig.title_description || ''"
                                        class="w-100"
                                    >  <!-- TODO - have defaults for this and content -->
                                        <b-input-group>
                                            <b-form-input
                                                v-model="eas.title"
                                                type="text"
                                                class=""
                                            ></b-form-input>
                                        </b-input-group>
                                    </b-form-group>
                                    <b-form-group 
                                        v-if="showContentInput"
                                        :label="selectedConfig.content_label || 'Reminder Content'"
                                        label-class="eas-form-label"
                                        :description="selectedConfig.content_description || ''"
                                        class="w-100 mb-0"
                                    >
                                        <b-input-group>
                                            <b-form-textarea
                                                v-model="eas.content"
                                                rows="6"
                                                class=""
                                            ></b-form-textarea>
                                        </b-input-group>
                                    </b-form-group>
                                </b-form-group>
                            </div>
                            <div class="w-100 d-flex align-items-center justify-content-end gap-3">
                                <b-button variant="secondary" size="md" :disabled="anyLoading" class="px-3" @click="cancelEasEdit">
                                    <b-icon icon="x-circle" class="mr-2"></b-icon> Cancel
                                </b-button>
                                <!-- TODO - allow updates -- at least so errors can get fixed! - then re-enable save button for isPersisted -->
                                <b-button variant="primary" size="md" :disabled="anyLoading || isPersisted" class="px-3" @click="save">
                                    <b-icon icon="check-circle" class="mr-2"></b-icon> Save
                                </b-button>
                            </div>
                        </div>
                    </div>
                </b-overlay>
            </div>
            <div v-else :key="`${eas.localId}_view`" class="">
                <div class="d-flex align-items-center justify-content-between gap-4">
                    <div class="d-flex align-items-center gap-2">
                        <!-- TODO - show tooltip with errors? -->
                        <b-icon v-if="eas.has_validation_error" key="error-icon" :id="`error-icon-${eas.localId}`" icon="x-circle-fill" variant="danger" v-b-popover.hover.top="eas.last_validation_error" title="Configuration Error"></b-icon>
                        <b-icon v-else-if="!eas.is_active" key="inactive-icon" icon="x-circle-fill" variant="secondary"></b-icon>
                        <b-icon v-else key="active-icon" icon="calendar" variant="accent"></b-icon>
                        <div class="">{{ eas.display_text }}</div>
                    </div>
                    <div class="d-flex align-items-center gap-2">
                        <b-button-group v-if="!readOnly">  
                            <b-button variant="gray-primary" size="sm" :disabled="anyLoading || showPendingDelete" @click="startEditExisting">
                                <b-icon icon="eye"></b-icon>
                            </b-button>
                            <b-button variant="gray-danger" size="sm" :disabled="anyLoading || showPendingDelete" @click="startDeleteAction">
                                <b-icon icon="trash"></b-icon>
                            </b-button>
                        </b-button-group>
                    </div>
                </div>
                <transition name="fade-fast">
                    <div v-if="showPendingDelete" class="d-flex align-items-center justify-content-center gap-3">
                        <div class="text-danger">Delete this reminder action?</div>
                        <b-button variant="danger" size="sm" @click="performDeleteAction">Yes, Delete</b-button>
                        <b-button variant="secondary" size="sm" @click="hideDeleteConfirmation">No, Cancel</b-button>
                    </div>
                </transition>
            </div>
        </transition>
    </div>
</template>

<script>
import { suppliosApi } from '@/js/api-service';
import { errorHandling, currentUserInfoMixin } from '@/js/custom-vue-mixins';
import { ExpireActionSpec } from './shared/compModels';
import cloneDeep from 'lodash/cloneDeep';
import EmailAddressListInput from './shared/EmailAddressListInput.vue';

export default {
    name: 'ExpireActionSingle',
    components: {
        EmailAddressListInput
    },
    mixins: [
        errorHandling, 
        currentUserInfoMixin,
    ],
    props: {
        // pass in ONE of compItemId or vendorCompItemId -- NOT BOTH
        // must make sure they share the same endpoints for API (shared methods here)
        // NOW WE JUST GET FROM easData, so we don't need these props anymore
        // compItemId: {
        //     type: String,
        //     required: false,
        // },
        // vendorCompItemId: {
        //     type: String,
        //     required: false,
        // },
        readOnly: {  // if true, disable all editing AND adding (will set localNoAdd to true)
            type: Boolean,
            default: false,
        },        
        easData: {  // should be an ExpireActionSpec object - data provided by the parent... we should NOT mutate this directly, we make local copy and edit copy?
            type: ExpireActionSpec,  // or a plain object that can be converted to ExpireActionSpec?
            required: true,
        },
        narrow: {  // if true, use a narrower layout for the single EAS - set true if you want to use this in a sidebar or similar
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            lastSavedEas: null,  // used to track last saved EAS, provided by parent?  or just use this.easData?
            eas: null,  // our local copy... set in created()
            localEditMode: false,
            saving: false,  // loading state while saving
            deleting: false,  // loading state while deleting
            showPendingDelete: false,  // used to show/hide pending delete confirmation
            initEdit: true,  // used to keep things in 'loading' state on first render
            // OPTIONS FOR THE FORM
            configs: [],  // our new configs/presets -- only for creates???  probably should either use this as the only 'type' or not at all
            selectedConfig: null,  // selected config from the configs list, if any
            selectedConfigReady: false,  // set to true once we have selected a config and set all the options based on it
            loadingConfigs: false,
            initialConfigsLoaded: false,  // set to true once we have loaded the configs at least once
            // action_types: [],
            event_type_options: [],
            recipient_type_options: [],
            recipient_object_role_options: [],
            schedule_type_options: [],
            duration_period_options: [],  // now also from UI
        }
    },
    // watch: {
    //     eas: {
    //         handler(newVal) {
    //             // watch for changes to eas, and update selected options if needed
    //             console.log('EAS changed', newVal);
    //             // TODO - update selected options based on new eas data -- or we can just do it once in created, then at least we'll always have something selected, user can make sure it's correct?
    //         },
    //         deep: true,  // deep watch to catch nested changes
    //     },
    // },
    computed: {
        compItemId() {
            // we get from data instead of separate prop 
            return this.easData?.comp_item_id || '';
        },
        vendorCompItemId() {
            // we get from data instead of separate prop 
            return this.easData?.vendor_comp_item_id || '';
        },
        isCompItem() {
            return !!this.compItemId && !this.vendorCompItemId;  // if we have compItemId, but not vendorCompItemId, we are a comp item
        },
        isVendorCompItem() {
            return !!this.vendorCompItemId;
        },
        mustSelectConfig() {
            return !this.isPersisted && !this.selectedConfig && this.initialConfigsLoaded && this.configs.length > 0;  // if we are not persisted and have configs, we must select one
        },
        loadedNoConfigs() {
            return this.initialConfigsLoaded && this.configs.length === 0;
        },
        showConfigSelectArea() {
            // show the config selection div -- could be that we have one to select, or that we have none and need to show message
            return !this.selectedConfigReady && (this.mustSelectConfig || this.loadedNoConfigs);  // show config select if we must select one, not in edit mode, and not read-only
        },
        isPersisted() {
            // check if the eas is persisted (saved to DB already)
            return !!this.eas?.id;
        },
        anyLoading() {
            return this.initEdit || this.loadingConfigs || this.saving || this.deleting;
        },
        currentTuId() {
            return this.$currentUserInfo?.tenant_user_id || '';
        },
        recipientTuOptions() {
            // return the options for the recipient_tu_id select - only if we have a current TU ID
            // TODO - get from API later!
            if (this.currentTuId) {
                return [{ label: 'Me', value: this.currentTuId }];
            }
            return [];
        },
        // FIELD VISIBILITY in EDIT MODE based on selected config
        showRecipientEmailInput() {
            return this.selectedConfig?.use_recipient_emails || false;
        },
        showTitleInput() {
            return this.selectedConfig?.use_title || false;
        },
        showContentInput() {
            return this.selectedConfig?.use_content || false;
        },
        useCompItemRole() {
            // should we use the comp item role select?  only if the recipient type allows it 
            // also used to clear out the value before sending to API if not using it
            return this.eas?.recipient_type_value === 'COMP_ITEM_ROLE';  // also check if we have any role options?  if no options, that's a problem...
        },
        useSpecificTu() {
            // should we use the specific TU select?  only if the recipient type allows it 
            // also used to clear out the value before sending to API if not using it
            return this.eas?.recipient_type_value === 'SPECIFIC_TU';  // also check if we have any TU options?  if no options, that's a problem...
        },
    },
    created() {
        this.lastSavedEas = cloneDeep(this.easData);  // keep a copy of the last saved EAS
        this.eas = this.lastSavedEas;  // or clone one right away?  use resetEas() to do that...
        // check if something passed in, or on the EAS object is set to enable edit mode?
        // make sure we have all things selected if there are options available, only if not set already 
        this.setUnsetValuesFromOptions();   // we might not have much/anything yet... we will call this after fetching options also
        if (!this.isPersisted) {  // if it's a NEW EAS, we should start in edit mode, otherwise parent does not tell us to edit, that's handled locally
            this.startEditAction();  // gotta use this so it fetches the options from the API and so parent can track the edit start
        } else {
            this.initEdit = false;  // we are done initEdit, we have the data
        }
    },
    mounted() {
        
    },
    methods: {
        resetEas() {
            // reset eas to the last saved EAS, either because we are about to edit, or because we are canceling an edit
            this.eas = new ExpireActionSpec(cloneDeep(this.lastSavedEas));
        },
        async fetchEasOptions() {
            // get data from API to show in the form for what's possible
            // we fetch this for EACH one individually so we can show the options that are available for each specific item
            // NOTE: this should maybe be done in PARENT?  since we need to have a selected config for each one, and we don't allow changing the config/action type on updates anyways
            //       maybe later... because we still need to get the options for the selected config when doing updates (for the options within one config) so need to call this endpoint anyways, both places
            this.loadingConfigs = true;
            let resp;
            try {
                // TODO - make it specific to the existing EAS if you have one????  separate for create/update?
                // NOTE: if you pass both of these, it will only use the more specific one (VCI over CI)
                resp = await suppliosApi.compEas.get_eas_options(this.compItemId, this.vendorCompItemId);
            } catch (e) {
                this.apiErrorHandler(e);
            }
            if (resp?.data) {
                console.log('expire action options', resp.data);
                this.configs = resp.data.configs || [];  // these are the presets, if any
            }
            // this.setUnsetValuesFromOptions(); // do all this after selecting config
            this.initialConfigsLoaded = true;
            this.loadingConfigs = false;
        },
        setOptionsFromSelectedConfig() {
            // set options based on the selectedConfig
            if (this.selectedConfig) {
                // if we have a selected config, set the options based on it
                this.schedule_type_options = this.selectedConfig.schedule_type_options || [];
                this.event_type_options = this.selectedConfig.event_type_options || [];
                this.duration_period_options = this.selectedConfig.duration_period_options || [];
                this.recipient_type_options = this.selectedConfig.recipient_type_options || [];
                this.recipient_object_role_options = this.selectedConfig.recipient_object_role_options || [];
            } else {
                // if no selected config, clear the options
                this.schedule_type_options = [];
                this.event_type_options = [];
                this.duration_period_options = [];
                this.recipient_type_options = [];
                this.recipient_object_role_options = [];
            }
            console.log('set options from selected config', {
                schedule_type_options: this.schedule_type_options,
                event_type_options: this.event_type_options,
                duration_period_options: this.duration_period_options,
                recipient_type_options: this.recipient_type_options,
                recipient_object_role_options: this.recipient_object_role_options,
            });
        },
        getSelectableOptions(optionsList) {
            // filter any of our options lists to only those that are not disabled and is_active=true if it has that flag
            return optionsList.filter(opt => {
                if ('disabled' in opt && opt.disabled) return false;  // if it has disabled flag and it's true, filter it out
                if ('is_active' in opt && !opt.is_active) return false;  // if it has is_active flag and it's false, filter it out
                return true;  // if it doesn't have disabled flag, then it will be included
            });
        },
        setUnsetValueFromOptionSingle(easFieldName, optionsList) {
            // set a single field value based on the options available, if not already set
            // call this after fetching options, to make sure we have the latest data
            const selectableOptions = this.getSelectableOptions(optionsList);
            const isUnset = [null, undefined, ''].includes(this.eas[easFieldName]);  // null, undefined, or empty string are all considered unset for our purposes here
            if (selectableOptions.length > 0 && isUnset) {
                // if we have options, and the field is not already set, set it to the first option's value
                const firstOptionValue = selectableOptions[0].value;
                console.log(`setting eas field ${easFieldName} to`, firstOptionValue);
                this.$set(this.eas, easFieldName, firstOptionValue);
            }
        },
        setUnsetValuesFromOptions() {
            console.log('setUnsetValuesFromOptions called');
            // set the eas values based on the options available, if not already set
            // call this after fetching options, to make sure we have the latest data

            this.setUnsetValueFromOptionSingle('recipient_tu_id', this.recipientTuOptions);
            this.setUnsetValueFromOptionSingle('recipient_object_role_id', this.recipient_object_role_options);
            this.setUnsetValueFromOptionSingle('duration_period', this.duration_period_options);
            this.setUnsetValueFromOptionSingle('event_type_value', this.event_type_options);
            this.setUnsetValueFromOptionSingle('schedule_type_value', this.schedule_type_options);
            this.setUnsetValueFromOptionSingle('recipient_type_value', this.recipient_type_options);
            // this.setUnsetValueFromOptionSingle('action_type_value', this.action_types);
            // OLD WAY.... kept for reference for now
            // const selectableRecipientTuOptions = this.getSelectableOptions(this.recipientTuOptions);
            // if (!this.eas.recipient_tu_id && selectableRecipientTuOptions.length > 0) {
            //     // if we have recipient TU options, set the first one as selected, if not already set
            //     console.log('setting recipient_tu_id to', selectableRecipientTuOptions[0].value);
            //     this.eas.recipient_tu_id = selectableRecipientTuOptions[0].value;
            // }
            // const selectableRecipientObjectRoleOptions = this.getSelectableOptions(this.recipient_object_role_options);
            // if (!this.eas.recipient_object_role_id && selectableRecipientObjectRoleOptions.length > 0) {
            //     // if we have recipient object roles, set the first one as selected, if not already set
            //     console.log('setting recipient_object_role_id to', selectableRecipientObjectRoleOptions[0].value);
            //     this.eas.recipient_object_role_id = selectableRecipientObjectRoleOptions[0].value;
            // }
            // const selectableDurationPeriodOptions = this.getSelectableOptions(this.duration_period_options);
            // if (!this.eas.duration_period && selectableDurationPeriodOptions.length > 0) {
            //     // if we have period options, set the first one as selected, if not already set
            //     console.log('setting duration_period to', selectableDurationPeriodOptions[0].value);
            //     this.eas.duration_period = selectableDurationPeriodOptions[0].value;
            // }
            // const selectableEventTypeOptions = this.getSelectableOptions(this.event_type_options);
            // if (!this.eas.event_type_value && selectableEventTypeOptions.length > 0) {
            //     // if we have event types, set the first one as selected, if not already set
            //     console.log('setting event_type_value to', selectableEventTypeOptions[0].value);
            //     this.eas.event_type_value = selectableEventTypeOptions[0].value;
            // }
            // const selectableScheduleTypeOptions = this.getSelectableOptions(this.schedule_type_options);
            // if (!this.eas.schedule_type_value && selectableScheduleTypeOptions.length > 0) {
            //     // if we have schedule types, set the first one as selected, if not already set
            //     console.log('setting schedule_type_value to', selectableScheduleTypeOptions[0].value);
            //     this.eas.schedule_type_value = selectableScheduleTypeOptions[0].value;
            // }
            // // if (!this.eas.action_type_value && this.action_types.length > 0) {
            // //     // if we have action types, set the first one as selected, if not already set
            // //     this.eas.action_type_value = this.action_types[0].value;
            // // }
            // const selectableRecipientTypeOptions = this.getSelectableOptions(this.recipient_type_options);
            // if (!this.eas.recipient_type_value && selectableRecipientTypeOptions.length > 0) {
            //     // if we have recipient types, set the first one as selected, if not already set
            //     console.log('setting recipient_type_value to', selectableRecipientTypeOptions[0].value);
            //     this.eas.recipient_type_value = selectableRecipientTypeOptions[0].value;
            // }
        },
        async save() {
            // save the localData to the API?  then emit an event to the parent to update its copy (and re-render) -- use a key based on modifed date?
            // update the data prop with the new data
            // close the modal
            this.saving = true;
            const apiData = this.eas.getDataForApi();
            // validate required fields????
            // make sure to clear out fields we are not using based on the recipient type
            if (!this.useCompItemRole) {
                apiData.recipient_object_role_id = null;
            }
            if (!this.useSpecificTu) {
                apiData.recipient_tu_id = null;
            }
            // do the same with title/content if not using them?  maybe... for now we will just send whatever is there
            const localId = this.eas.localId;  // keep the local ID to use for the new EAS object
            // TODO - need to figure out what data from EAS Config we need to send to the API
            // for now, if its a create (not persisted), we will manually add the 'type_key' to the data
            if (!this.isPersisted && this.selectedConfig) {
                // if we are creating a new EAS, and have a selected config, add the type_key to the data
                apiData.type_key = this.selectedConfig.type_key;  // this is the key for the config we are using
            }
            let newEas;
            try {
                if (this.isPersisted) {
                    // update via API
                    let updateResp;
                    if (this.isCompItem) {
                        updateResp = await suppliosApi.compItemStd.update_expire_action_spec(this.compItemId, apiData);
                    } else if (this.isVendorCompItem) {
                        updateResp = await suppliosApi.vendorCompItemStd.update_expire_action_spec(this.vendorCompItemId, apiData);
                    }
                    // const updateResp = await suppliosApi.compItemStd.update_expire_action_spec(this.compItemId, apiData);
                    newEas = new ExpireActionSpec({localId, ...updateResp.data});
                } else {
                    // create via API
                    let createResp;
                    if (this.isCompItem) {
                        createResp = await suppliosApi.compItemStd.create_expire_action_spec(this.compItemId, apiData);
                    } else if (this.isVendorCompItem) {
                        createResp = await suppliosApi.vendorCompItemStd.create_expire_action_spec(this.vendorCompItemId, apiData);
                    }
                    // const createResp = await suppliosApi.compItemStd.create_expire_action_spec(this.compItemId, apiData);
                    newEas = new ExpireActionSpec({localId, ...createResp.data});
                }
                this.eas = newEas;  // update the local eas with the new data from the API
                this.lastSavedEas = cloneDeep(this.eas);  // update the last saved eas with the new data
                this.$emit('updated', this.eas);  // emit the updated event to the parent
                console.log('saved eas', this.eas);
            } catch (error) {
                this.apiErrorHandler(error);
                this.saving = false;
                return;  // stop execution if there was an error
                
            }
            // if (newEas) {
            //     this.$emit('updated', this.eas);
            // }
            this.editStopped();
            this.saving = false;
        },
        async startEditAction() {
            await this.fetchEasOptions();
            this.resetEas();  // make a fresh copy of the last saved EAS, that's what we'll edit
            this.editStarted();  // set localEditMode to true, and emit event to parent
            this.initEdit = false;  // we are done initializin
        },
        async startEditExisting() {
            // start editing an existing EAS
            // this is basically just startEditAction, then we select the config based on the existing EAS data
            await this.fetchEasOptions();  // need this to get configs first
            this.resetEas();  // make a fresh copy of the last saved EAS, that's what we'll edit -- before or after settings configForExisting?  
            const configForExisting = this.configs.find(cfg => cfg.type_key === this.eas.action_type_value);
            if (!configForExisting) {
                console.error(`Could not find config for existing EAS to edit using type_key ${this.eas.action_type_value}`);
                return;
            }
            this.editStarted();  // set localEditMode to true, and emit event to parent
            this.initEdit = false;  // we are done initializin
            this.handleConfigSelect(configForExisting);  // need editStarted first so we are in edit mode
        },
        cancelEasEdit() {
            // NOTE - has to handle both the case where we are NEW (not yet saved/persisted) and the case where we are editing an existing EAS
            console.log('will cancelEasEdit');
            // must tell parent edit-stopped, so it can reset its local state -- in both cases
            this.editStopped();
            if (!this.isPersisted) {
                // if not persisted, we basically delete ourselves... tell parent to delete us
                this.emitDeleted();  // tell parent that this EAS was deleted, we will be destroyed
            } else {
                // if we are editing an existing EAS, we just reset to the last saved EAS
                // this will also reset the localEditMode to false
                this.resetEas();  // reset eas to the last saved EAS
            }
        },
        // NOTE: may no longer need to tell parent about edit started/stopped, we're still doing it, but parent doesn't currently do anything with it 
        //     (but it could... might actually be useful there, which is why we're keeping it for now)
        editStarted() {
            this.localEditMode = true;
            this.$emit('edit-started', this.eas);
            console.log('edit started on eas', this.eas);
        },
        editStopped() {
            this.localEditMode = false;
            this.$emit('edit-stopped', this.eas);
        },
        emitDeleted() {
            // tell parent that this EAS was deleted
            this.$emit('deleted', this.eas);
        },
        startDeleteAction() {
            // show confirmation?  then delete from API, and remove from local store
            console.log('starting delete confirmation on eas');
            this.showPendingDelete = true;
            // do we need to emit edit-started?  skipping that for now....
        },
        hideDeleteConfirmation() {
            this.showPendingDelete = false;
        },
        async performDeleteAction() {
            // show confirmation?  then delete from API, and remove from local store
            if (this.readOnly) {
                this.cancelEasEdit();
                this.hideDeleteConfirmation();
                return;
            }
            console.log('will delete eas');
            this.deleting = true;
            const postData = { id: this.eas.id };
            // TODO - delete via API
            let resp;
            try {
                if (this.isCompItem) {
                    resp = await suppliosApi.compItemStd.delete_expire_action_spec(this.compItemId, postData);
                } else if (this.isVendorCompItem) {
                    resp = await suppliosApi.vendorCompItemStd.delete_expire_action_spec(this.vendorCompItemId, postData);
                }
                // const resp = await suppliosApi.compItemStd.delete_expire_action_spec(this.compItemId, postData);
                console.log('delete response', resp);  // no real response data
            } catch (error) {
                this.apiErrorHandler(error);
            }
            // this.cancelEasEdit();
            this.deleting = false;
            this.hideDeleteConfirmation();
            // this.emitStopped();  // may not need this, if not emitting edit-started
            this.emitDeleted();  // parent will handle the deletion from its store, and this instance will be destroyed
        },
        handleConfigSelect(config) {
            // handle selecting a config from the configs list
            console.log('selected config', config);
            const configDefaults = this.isPersisted ? {} : config?.default_values || {};  // only use defaults if we are NOT persisted (creating new)
            this.eas = new ExpireActionSpec({ ...this.eas, ...configDefaults });  // merge the config into the eas data -- we probably only need action_type_value -- unless we have separate defaults nested...
            console.log('eas after merging config', JSON.stringify(this.eas, null, 2));
            // TODO - should probably have a 'default' eas hanging off the config, and merge THAT in instead of the full config 
            this.selectedConfig = config;  // set the selected config last, so we don't change mode until we are done setting things
            // need to populate the options available, and set the values if there is only one option available
            this.setOptionsFromSelectedConfig();
            this.setUnsetValuesFromOptions();  // make sure we have all values set based on the options available
            // this.editStarted();  // don't need this on first create, right?
            this.selectedConfigReady = true;  // we are ready to show the form now
        },
    },
}
</script>

<style lang="scss">
.expire-action-spec-single {
    border: 1px solid var(--gray-300);
    background: #fff; // var(--gray-50);
    border-radius: 0.5rem;
    padding: 8px 8px;
    //margin-bottom: 6px;
    &.locked {
        opacity: 0.7;
    }
    &.editing {
        opacity: 1;
        background-color: rgba(128, 128, 128, 0.1);
    }
    &.pending-delete {
        background-color: var(--danger-50);
    }
    &.init-edit {
        background-color: rgba(128, 128, 128, 0.1);
    }
    .eas-form-label {
        font-weight: 500;
        padding-bottom: 0;
        color: var(--dark-800);
        font-size: 0.8rem;
        &.group-label {
            font-size: 0.9rem;
            color: var(--primary-700);
            margin-bottom: 0.5rem;
        }
    }
}
</style>